import { getErrorSupportMetadata } from 'helpers/apiError'
import React, { useState, useEffect } from 'react'
import Select from 'components/select'
import useProject from 'hooks/useProject'
import { useShownHub } from 'hooks/useShownHub'
import api, { NewPolicyRequest } from 'helpers/api'
import TagPermissions from 'components/permissions/views/tag'
import UserPermissions from 'components/permissions/views/user'
import { IAccessPolicy } from 'types'
import { AccessResourceKinds, UserRoles, AccessAttributeKinds, PROJECT } from 'app-constants'
import { uuidv4 } from 'helpers/utils'
import Button from 'components/button'
import FilterMenu, { FIELD_MENU_BUTTON, FilterGroup } from 'components/filter-menu'
import Tabs from 'components/tabs'
import ActionDialog from 'components/dialog/action'
import { FieldRow, SettingsCard } from 'views/settings/components/settingsCard'
import { useApplicationStore } from 'hooks/application'
import { formatDateTime } from 'components/spreadsheet/helpers/format'

interface IResource {
  label: string
  value: string
  group?: string
}

// The resource types, as the type menu names them, and their nouns for the resources field and its count.
const TYPES: Array<{ value: AccessResourceKinds; label: string; one: string; many: string }> = [
  { value: AccessResourceKinds.PROCESS, label: 'Documents', one: 'document', many: 'documents' },
  { value: AccessResourceKinds.TABLE, label: 'Tables', one: 'table', many: 'tables' },
  { value: AccessResourceKinds.VIEW, label: 'Views', one: 'view', many: 'views' }
]

/** The resources as the menu lists them: views under their table, the rest in one list. */
const resourceGroups = (resources: IResource[]): FilterGroup[] => {
  const labels = [...new Set(resources.map((resource) => resource.group))]
  return labels.map((label) => ({
    label,
    options: resources
      .filter((resource) => resource.group === label)
      .map((resource) => ({ value: resource.value, label: resource.label }))
  }))
}

/**
 * Replaces the permissions of chosen documents, tables or views with ones set here, in the Styling page's
 * card: the type, the resources (with a search), the new permissions for people and for tags, and Update
 * Permissions, which asks to confirm as the current permissions are lost.
 */
const BulkPermissionsUpdate: React.FC = () => {
  const { project } = useProject()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [resourceType, setResourceType] = useState<AccessResourceKinds>()
  const [resources, setResources] = useState<IResource[]>()
  const [selectedResources, setSelectedResources] = useState<string[]>([])
  const [loadingResources, setLoadingResources] = useState<boolean>(false)
  const [permissionTab, setPermissionTab] = useState<string>('user')
  const [policies, setPolicies] = useState<Array<IAccessPolicy>>([])
  const [savingPermissions, setSavingPermissions] = useState<boolean>(false)
  const [confirming, setConfirming] = useState(false)
  const stillShown = useShownHub(project.publicId)

  const excludedPermissionCategories =
    resourceType === AccessResourceKinds.PROCESS
      ? []
      : resourceType === AccessResourceKinds.TABLE
        ? [UserRoles.VIEWER, UserRoles.CONTRIBUTOR]
        : [UserRoles.OWNER]

  /** Lists the hub's views, table by table, while `current` says the load is still the one wanted. */
  const getProjectViews = async (current: () => boolean) => {
    const projectTables = await api.getProjectTables(project.publicId)
    if (projectTables.data) {
      for (const table of projectTables.data) {
        const projectViews = await api.getTableViews(table.publicId, { ignoreColumns: true })
        if (!current()) return
        if (projectViews.data) {
          const projectViewsOptions = projectViews.data.map((view) => {
            return {
              value: view.publicId,
              label: view.name,
              group: 'Table: ' + table.name + ' (' + formatDateTime(table.createdAt + 'Z', 'YYYY-MM-DD HH:mm') + ')'
            }
          })
          setResources((resources) => (resources ? [...resources, ...projectViewsOptions] : projectViewsOptions))
        }
      }
    }
  }

  useEffect(() => {
    // Another hub or type: start again, with no confirmation left open for the last one, and drop the
    // last load's resources when they arrive late.
    let cancelled = false
    const current = () => !cancelled
    setResources(undefined)
    setPolicies([])
    setSelectedResources([])
    setPermissionTab('user')
    setConfirming(false)
    if (project && project.publicId !== '' && resourceType) {
      setLoadingResources(true)
      if (resourceType === AccessResourceKinds.PROCESS) {
        api.getProjectProcesses(project.publicId).then((response) => {
          if (current() && response.data) {
            const projectProcesses = response.data.map((process) => {
              return {
                value: process.publicId,
                label: process.name + ' (' + formatDateTime(process.createdAt + 'Z', 'YYYY-MM-DD HH:mm') + ')'
              }
            })
            setResources(projectProcesses)
            setLoadingResources(false)
          }
        })
      } else if (resourceType === AccessResourceKinds.TABLE) {
        api
          .getProjectTables(project.publicId)
          .then((response) => {
            if (current() && response.data) {
              const projectTables = response.data.map((table) => {
                return {
                  value: table.publicId,
                  label: table.name + ' (' + formatDateTime(table.createdAt + 'Z', 'YYYY-MM-DD HH:mm') + ')'
                }
              })
              setResources(projectTables)
              setLoadingResources(false)
            }
          })
          .catch((e) => {
            if (current()) displayErrorMessage(e)
          })
      } else if (resourceType === AccessResourceKinds.VIEW) {
        getProjectViews(current).then(() => {
          if (current()) setLoadingResources(false)
        })
      }
    }
    return () => {
      cancelled = true
    }
  }, [project.publicId, resourceType])

  const handleCreateAccessPolicy = async (
    role: UserRoles,
    attributeKind: AccessAttributeKinds,
    attributeId?: string,
    tagReferenceId?: string
  ) => {
    if (resourceType) {
      const policy: IAccessPolicy = {
        accessAttribute: {
          kind: attributeKind,
          publicId: attributeId ? attributeId : 'AAA'
        },
        accessResource: {
          kind: resourceType,
          publicId: uuidv4()
        },
        publicId: uuidv4(),
        role,
        roleLabel: role === UserRoles.OWNER ? 'Owner' : role === UserRoles.CONTRIBUTOR ? 'Contributor' : 'Viewer'
      }

      if (attributeKind === AccessAttributeKinds.PROJECT && attributeId) {
        policy.accessAttribute['project'] = {
          publicId: project.publicId,
          name: project.name
        }
      } else if (attributeKind === AccessAttributeKinds.TAG && tagReferenceId) {
        policy.accessAttribute['tag'] = {
          publicId: '',
          value: '',
          projectPublicId: project.publicId,
          referencePublicId: tagReferenceId
        }
      } else if (attributeKind === AccessAttributeKinds.ALL_TABLE_TAGS && attributeId) {
        const table = await api.getTable(attributeId, true)

        if (table) {
          policy.accessAttribute['documentTable'] = {
            name: table.data.name,
            publicId: attributeId
          }
        } else {
          return
        }
      } else if (attributeKind === AccessAttributeKinds.USER && attributeId) {
        const user = await api.getUserByPublicId(attributeId)
        if (user) {
          policy.accessAttribute['user'] = {
            publicId: attributeId,
            name: user.data.name,
            firebaseUserId: user.data.firebaseUserId
          }
        } else {
          return
        }
      }
      setPolicies((policies) => [...policies, policy])
    }
  }

  const handleUpdateAccessPolicy = async (policy: IAccessPolicy, role: UserRoles) => {
    if (role == UserRoles.NO_ACCEESS) {
      setPolicies(policies.filter((p) => p.publicId !== policy.publicId))
    } else {
      setPolicies(
        policies.map((p) =>
          p.publicId === policy.publicId
            ? {
                ...policy,
                role,
                roleLabel:
                  role === UserRoles.OWNER ? 'Owner' : role === UserRoles.CONTRIBUTOR ? 'Contributor' : 'Viewer'
              }
            : p
        )
      )
    }
  }

  const handleDeleteAccessPolicy = async (policy: IAccessPolicy) => {
    setPolicies(policies?.filter((p) => p.publicId !== policy.publicId))
  }

  const handleBulkUpdatePolicy = async (
    role: UserRoles,
    attributeKind: AccessAttributeKinds,
    attributeIds?: string[],
    tagReferenceIds?: string[]
  ) => {
    if (!resourceType) return

    // 1. Remove all existing policies for this role + attribute kind + resource type
    setPolicies((prev) =>
      prev.filter(
        (p) =>
          !(p.role === role && p.accessAttribute?.kind === attributeKind && p.accessResource?.kind === resourceType)
      )
    )

    const hasAttributes = attributeIds && attributeIds.length > 0
    const hasTags = tagReferenceIds && tagReferenceIds.length > 0

    try {
      // 2. If there are attributeIds, create policies for them
      if (hasAttributes) {
        await Promise.all(attributeIds.map((id) => handleCreateAccessPolicy(role, attributeKind, id)))
      }

      // 3. If there are tagReferenceIds, create policies for them
      if (hasTags) {
        await Promise.all(
          tagReferenceIds.map((refId) => handleCreateAccessPolicy(role, attributeKind, undefined, refId))
        )
      }

      // If both arrays are empty or only passed as empty, step (1) alone
      // acts as "Remove all" for this role + kind.
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const bulkUpdatePermissions = async () => {
    if (!resources || !resourceType) return
    const hub = project.publicId
    setSavingPermissions(true)
    let updateNumber = 1

    for (let i = 0; i < selectedResources.length; i++) {
      const selectedResourceId = selectedResources[i]

      // Build policy objects outside try to avoid value blocks in try/catch
      const policyObjects: NewPolicyRequest[] = policies.map((policy) => {
        const tagRefId = policy.accessAttribute.tag ? policy.accessAttribute.tag.referencePublicId : undefined
        const attrPublicId = policy.accessAttribute.publicId
        return {
          attributeKind: policy.accessAttribute.kind,
          attributeId: attrPublicId === 'AAA' ? undefined : attrPublicId,
          role: policy.role,
          resourceKind: resourceType,
          resourceId: selectedResourceId,
          tagReferenceId: tagRefId,
          context: { projectId: hub }
        }
      })

      const successMsg = `Successfully updated resource ${updateNumber} of ${selectedResources.length}`
      const errorMsg = `Permissions couldn't be updated for item ${updateNumber} of ${selectedResources.length}. Try that item again.`

      // Read the current policies, create the new ones, then delete the old: a failure at any step is this
      // item's, said as such, and the rest carry on.
      const applyPolicies = async () => {
        const existingPolicies = await api.getResourcePermissions(resourceType, selectedResourceId)
        const oldPolicies = existingPolicies.data ? existingPolicies.data : []
        for (let j = 0; j < policyObjects.length; j++) {
          await api.createAccessPolicy(policyObjects[j])
        }

        for (let k = 0; k < oldPolicies.length; k++) {
          const policy = oldPolicies[k]
          await api.deleteAccessPolicy(policy.publicId, policy)
        }
      }

      try {
        await applyPolicies()
        // Progress is for the hub on show; a failure is said wherever you are.
        if (stillShown(hub)) setSnackbarMessage({ status: 'success', message: successMsg })
      } catch (error) {
        setSnackbarMessage({ status: 'error', message: errorMsg, ...getErrorSupportMetadata(error) })
      }

      // Increment the update number
      updateNumber++
    }

    setSavingPermissions(false)
    // Finished once another hub is shown: what's chosen there is its own, so it's left as it is.
    if (!stillShown(hub)) return
    setResourceType(undefined)
    setResources(undefined)
    setPolicies([])
    setSelectedResources([])
    setPermissionTab('user')
  }

  const type = TYPES.find((each) => each.value === resourceType)
  const ready = !!resourceType && !!resources && selectedResources.length > 0 && policies.length > 0
  const count = (n: number) => (type ? `${n} ${n === 1 ? type.one : type.many}` : '')

  return (
    <SettingsCard
      title="Update permissions"
      description="Replaces the permissions of the chosen documents, tables or views with the ones set below."
      footer={
        <>
          <p className="flex-1 text-muted">
            The chosen resources&apos; current permissions are replaced, and cannot be restored.
          </p>
          <Button
            data-tour="bulk-update-permissions-apply"
            internalType="danger"
            onClick={() => setConfirming(true)}
            disabled={!ready}
            isLoading={savingPermissions}
          >
            Update Permissions
          </Button>
        </>
      }
    >
      <FieldRow label="Resource type">
        <Select
          options={TYPES.map((each) => ({ value: each.value, label: each.label }))}
          optionsSelected={resourceType ? [resourceType] : []}
          onOptionClick={(option) => setResourceType(option as AccessResourceKinds)}
          placeholder="Choose documents, tables or views"
          searchable={false}
        />
      </FieldRow>

      {type && (
        <FieldRow label={type.label}>
          <FilterMenu
            noun={{ one: type.one, many: type.many }}
            groups={resourceGroups(resources ?? [])}
            selected={selectedResources}
            onChange={(chosen) => setSelectedResources(chosen ?? [])}
            pick
            loading={loadingResources}
            label={type.label}
            buttonLabel={
              selectedResources.length === 0 ? (
                <span className="text-faint">Choose {type.many}</span>
              ) : (
                count(selectedResources.length)
              )
            }
            buttonClassName={FIELD_MENU_BUTTON}
            matchWidth
          />
        </FieldRow>
      )}

      {type && selectedResources.length > 0 && (
        <FieldRow
          label="New permissions"
          description={`Who can open the ${count(selectedResources.length)}, by person, by everyone in the ${PROJECT}, or by tag.`}
        >
          <div className="flex flex-col gap-3">
            <div className="flex">
              <Tabs
                label="Permissions by"
                options={[
                  { value: 'user', label: 'General' },
                  { value: 'tags', label: 'Tags' }
                ]}
                value={permissionTab}
                onChange={setPermissionTab}
              />
            </div>
            <UserPermissions
              policies={policies}
              display={permissionTab === 'user'}
              onCreatePolicy={handleCreateAccessPolicy}
              onUpdatePolicy={handleUpdateAccessPolicy}
              excludedPermissionCategories={excludedPermissionCategories}
            />
            <TagPermissions
              policies={policies}
              display={permissionTab === 'tags'}
              onCreatePolicy={handleCreateAccessPolicy}
              onDeletePolicy={handleDeleteAccessPolicy}
              onBulkSetPolicies={handleBulkUpdatePolicy}
              excludedPermissionCategories={excludedPermissionCategories}
            />
          </div>
        </FieldRow>
      )}

      {confirming && type && (
        <ActionDialog
          title={`Update the permissions of ${count(selectedResources.length)}?`}
          description="Their current permissions are replaced with the ones set here. This cannot be undone."
          confirmLabel="Update Permissions"
          danger
          onClose={() => setConfirming(false)}
          onConfirm={bulkUpdatePermissions}
        />
      )}
    </SettingsCard>
  )
}

export default BulkPermissionsUpdate
